import React, { useMemo } from 'react';
import {
  Box, Field, FieldTypes, Inscription,
} from '@common-fe/common-fe';
import _ from 'lodash';
import * as yup from 'yup';

import { VALIDATORS } from '@/common';
import { INTEGER_VALUE_TEXT, INVALID_RANGE_TEXT, MUST_BE_GREATER_TEXT, REQUIRED_TEXT } from '@/common/constants';
import { useOrganizationOverridableProperties } from '@/modules/core/hooks/useOrganizationOverridableProperties';
import { PlanTypesEnum } from '@/modules/plan/plan.types';
import { useCurrentPlan, usePlanFieldsState, useTemplateFieldState } from '@/modules/plan/PlanSetup/hooks';
import { PLAN_SETUP_FIELD } from '@/modules/plan/PlanSetup/PlanSetup.constants';
import { usePreviewStore } from '@/modules/plan/PlanSetup/stores';
import theme from '@/styles/theme';
import useFieldsWithDefaultValues from '@/utils/hooks/useFieldsWithDefaultValues';

import { RunOutOption } from '../../../Coverage/GracePeriod/hooks/useGracePeriodFields';
import { useGracePeriodStore } from '../../../Coverage/GracePeriod/stores';
import { FUNDING_METHODS } from '../AccountFunding.types';
import FinancialHeader from '../FinancialHeader';
import { useAccountFundingParamsStore, useFinancialDetailsParamsStore } from '../stores';

import useBanksList from './useBanksList';
import useEOYPlanReconciliationFields from './useEOYPlanReconciliationFields';
import useFinancialDetailsParamsFields from './useFinancialDetailsParamsFields';
import { useFundingMethodsFeatureFlag } from './useFundingMethodsFeatureFlag';
import { usePayrollContributionFields } from './usePayrollContributionFields';

const DAILY_KEY = 'DAILY';
const WEEKLY_KEY = 'WEEKLY';
const MONTHLY_KEY = 'MONTHLY';
const HSA_PLAN_TYPE = 'HSA';
const CUSTOM_DAY_FIELD_HINT = 'If a month has fewer days than the entered number, replenishment will be processed on the last day of this month';
const CUSTOM_DAY_ERROR_TEXT = 'This number does not match the range of available numbers (1-31)';
const PRE_FUND_SUBTITLE = 'Elevate holds a portion of member enrollments to support quick claim payments. Each month elections will be re-calculated and determine if additional funds will need to be collected';
const PRE_FUND_SUBFIELDS = {
  PERCENT: 'The percentage of prefund depends on replenishment frequency',
  ELECTION_CHANGE_PERCENTAGE: 'Increased elections from the previous pre-fund where additional funds will be collected',
};

const APPLY_FEE_TO_OPTIONS = [
  { label: 'All Active Accounts (Canceled and Active Policies)', value: 'ACTIVE_ACCOUNTS_ALL_PLANS' },
  { label: 'Canceled Plans with Active Accounts', value: 'ACTIVE_ACCOUNTS_CANCELED_PLANS' },
];


export enum OMNIBUS_TYPES {
  NOTIONAL = 'NOTIONAL',
  HSA = 'HSA',
}

const OMNIBUS_OPTIONS = [
  { key: OMNIBUS_TYPES.NOTIONAL, value: 'Notional' },
  { key: OMNIBUS_TYPES.HSA, value: 'HSA' },
];

const currencyValueValidate = (minValue = 1, maxValue = 999999) => yup
  .string()
  .test({
    test: (val) => {
      if (val) {
        const value = Number(val);
        return value >= minValue && value <= maxValue;
      }
      return true;
    },
    message: INVALID_RANGE_TEXT(minValue, maxValue),
  })
  .test({
    test: (val) => {
      if (val) {
        const value = Number(val);
        return Number.isInteger(value);
      }
      return true;
    },
    message: INTEGER_VALUE_TEXT,
  });

const percentValueValidate = yup
  .string()
  .test({
    test: (val) => {
      if (val) {
        const value = Number(val);
        return value >= 1 && value <= 100;
      }
      return true;
    },
    message: INVALID_RANGE_TEXT(1, 100),
  });

const FIELD_WRAPPER_STYLES = {
  borderTop: `1px solid ${theme.colors.border1}`,
  paddingTop: theme.spacings.m,
};
const ALTERNATIVE_FIELD_WRAPPER_STYLES = {
  borderBottom: `1px solid ${theme.colors.border1}`,
  paddingBottom: theme.spacings.m,
};

const useAccountFundingParamsFields = (
  isActivationMode?: boolean,
  isTemplateMode?: boolean,
  isEditMode?: boolean,
) => {
  const { runOut } = useGracePeriodStore((state) => state.state);
  useBanksList();
  const sourceState = useAccountFundingParamsStore((state) => state.sourceState);
  const currentState = useAccountFundingParamsStore((state) => state.state);
  const isForfeitAllowed = useMemo(() => currentState?.isForfeitAllowed, [currentState]);
  const { planType } = useCurrentPlan();
  const isFundingMethodsFF = useFundingMethodsFeatureFlag();

    const {
      observingOrganizationData,
    } = useOrganizationOverridableProperties();

  const orgPrefundValues = useMemo(() => {
    return {
      replenishmentMinValue: observingOrganizationData?.replenishmentMinValue
        ? `${observingOrganizationData.replenishmentMinValue}`
        : undefined,
      replenishmentPercent: observingOrganizationData?.replenishmentPercent
        ? `${observingOrganizationData.replenishmentPercent}`
        : undefined,
      replenishmentCreepMin: observingOrganizationData?.replenishmentCreepMin
        ? `${observingOrganizationData.replenishmentCreepMin}`
        : undefined,
      replenishmentCreepPercent: observingOrganizationData?.replenishmentCreepPercent
        ? `${observingOrganizationData.replenishmentCreepPercent}`
        : undefined,
    };
  }, [observingOrganizationData]);

  const fundingMethod = useMemo(() => currentState?.fundingMethod, [currentState]);

  const isParticipantPaidMaintenanceFee = useMemo(() => currentState?.isParticipantPaidMaintenanceFee, [currentState]);
  const isPlanLevelFunding = useFinancialDetailsParamsStore((state) => state.state.planLevelFunding);
  const isPreFundCalc = useFinancialDetailsParamsStore((state) => state.state.isPreFundCalculated);
  const replenishmentFrequency = useAccountFundingParamsStore(
    (state) => state.state.replenishmentFrequency,
  );

  const isForfeitDisabled = useMemo(() => planType === PlanTypesEnum.HSA ||
    runOut === RunOutOption.NO_CLAIMS_DEADLINE, [runOut, planType]);

  const previewMode = usePreviewStore((state) => state.previewMode);
  const appliedSourceState = useMemo(() => {
    if (previewMode) {
      return currentState;
    }
    return sourceState;
  }, [currentState, previewMode, sourceState]);

  const fundingReplenishmentFields = useMemo(() => {
    if (isPlanLevelFunding && fundingMethod === FUNDING_METHODS.STANDARD_NOTIONAL && !isTemplateMode) {
      let replenishmentFields: Field[] = [];
      if (replenishmentFrequency === WEEKLY_KEY) {
        replenishmentFields = [{
          name: 'replenishmentDayNumber',
          type: FieldTypes.Dropdown,
          label: 'Day of the week',
          singleMode: true,
          options: [
            { key: '1', value: 'Monday' },
            { key: '2', value: 'Tuesday' },
            { key: '3', value: 'Wednesday' },
            { key: '4', value: 'Thursday' },
            { key: '5', value: 'Friday' },
          ],
          placeholder: 'Select frequency',
          showRequireIcon: true,
          fieldWrapperStyles: ALTERNATIVE_FIELD_WRAPPER_STYLES,
          validator: VALIDATORS.REQUIRED_STRING,
        }];
      }
      if (replenishmentFrequency === MONTHLY_KEY) {
        replenishmentFields = [{
          name: 'replenishmentDayNumber',
          label: 'Custom',
          type: FieldTypes.Number,
          lowerHelpText: CUSTOM_DAY_FIELD_HINT,
          showRequireIcon: true,
          fieldWrapperStyles: ALTERNATIVE_FIELD_WRAPPER_STYLES,
          placeholder: 'Enter custom day number',
          validator: VALIDATORS.REQUIRED_STRING.test({
            exclusive: false,
            test: (val) => {
              const customDayNumber = _.toNumber(val);
              return customDayNumber > 0 && customDayNumber <= 31;
            },
            message: CUSTOM_DAY_ERROR_TEXT,

          }).required(REQUIRED_TEXT),
        }];
      }
      return [
        {
          name: 'financeTitle',
          type: FieldTypes.Node,
          fieldWrapperStyles: FIELD_WRAPPER_STYLES,
          editMode: true,
          value: (
            <FinancialHeader
              title="Funding Replenishment"
              subTitle="Funds collected to replenish the plans account after claim payment have been made."
            />
          ),
        },
        // {
        //   name: 'replenishmentBankAccount',
        //   type: FieldTypes.Dropdown,
        //   label: 'Bank account',
        //   singleMode: true,
        //   options: banksList,
        //   placeholder: 'Select bank account',
        //   showRequireIcon: true,
        //   validator: VALIDATORS.REQUIRED_STRING,
        // }, {
        //   name: 'replenishmentMethod',
        //   type: FieldTypes.Radio,
        //   label: 'Method',
        //   options: FUNDING_REPLENISHMENT_METHOD_OPTIONS,
        //   defaultValue: FUNDING_REPLENISHMENT_METHOD_OPTIONS[0].value,
        //   showRequireIcon: true,
        //   disabled: true,
        //   // validator: VALIDATORS.REQUIRED_STRING,
        // }, 
        {
          name: 'replenishmentFrequency',
          type: FieldTypes.Radio,
          label: 'Frequency',
          options: [
            { label: 'Daily', value: DAILY_KEY },
            { label: 'Weekly', value: WEEKLY_KEY },
            { label: 'Monthly', value: MONTHLY_KEY },
          ],
          showRequireIcon: true,
          // fieldWrapperStyles: replenishmentFrequency === DAILY_KEY ? FIELD_WRAPPER_STYLES : {},
          validator: VALIDATORS.REQUIRED_STRING,
        },

        ...replenishmentFields,
      ] as Field[];
    }
    return [];
  }, [isPlanLevelFunding, fundingMethod, isTemplateMode, replenishmentFrequency]);

  const preFundFields = useMemo(() => {
    // const isBankAccountFieldsVisible = fundingMethod === FUNDING_METHODS.PREFUND_SPENDDOWN
    //   || fundingMethod === FUNDING_METHODS.PREFUND_AND_CONTRIBUTION;
    if (isPreFundCalc && fundingMethod !== FUNDING_METHODS.CONTRIBUTION_ONLY) {
      return [
        ...(isPreFundCalc ? [{
          name: 'preFundTitle',
          type: FieldTypes.Node,
          editMode: true,
          // fieldWrapperStyles:FIELD_WRAPPER_STYLES,
          value: (
            <FinancialHeader
              title="Pre-Fund"
              viewMode={!isEditMode}
              subTitle={fundingMethod !== FUNDING_METHODS.PREFUND_SPENDDOWN ? PRE_FUND_SUBTITLE : undefined}
            />
          ),
        },
        // ...(isBankAccountFieldsVisible && !isTemplateMode) ? [
        //   {
        //     name: 'replenishmentBankAccount',
        //     type: FieldTypes.Dropdown,
        //     label: 'Bank account',
        //     singleMode: true,
        //     options: banksList,
        //     placeholder: 'Select bank account',
        //     showRequireIcon: true,
        //     validator: VALIDATORS.REQUIRED_STRING,
        //   }, {
        //     name: 'replenishmentMethod',
        //     type: FieldTypes.Radio,
        //     label: 'Method',
        //     options: FUNDING_REPLENISHMENT_METHOD_OPTIONS,
        //     defaultValue: FUNDING_REPLENISHMENT_METHOD_OPTIONS[0].value,
        //     showRequireIcon: true,
        //     disabled: true,
        //     validator: VALIDATORS.REQUIRED_STRING,
        //   }
        // ] : [],
        ...fundingMethod === FUNDING_METHODS.PREFUND_SPENDDOWN ? [{
          name: 'replenishmentPercentSpenddown',
          type: FieldTypes.Number,
          disabled: true,
          showRequireIcon: true,
          placeholder: 'Select Percent',
          value: 100,
          label: (
            <Box flex={{ grow: 1 }}>
              <Inscription
                weight="bold"
                color="textBody"
              >
                Percent
              </Inscription>
            </Box>
          ),
        }] : [],
        ...fundingMethod !== FUNDING_METHODS.PREFUND_SPENDDOWN ? [
          {
            name: PLAN_SETUP_FIELD.REPLENISHMENT_PERCENT,
            type: FieldTypes.Number,
            disabled: !isEditMode,
            showRequireIcon: true,
            validator: percentValueValidate.required(REQUIRED_TEXT),
            placeholder: 'Select Percent',
            value: sourceState?.replenishmentPercent || orgPrefundValues?.replenishmentPercent,
            label: (
              <Box flex={{ grow: 1 }}>
                <Inscription
                  weight="bold"
                  color="textBody"
                >
                  Percent
                </Inscription>
                <Box>
                  <Inscription weight={500} color="textSecondary" size="small">
                    {PRE_FUND_SUBFIELDS.PERCENT}
                  </Inscription>
                </Box>
              </Box>
            ),
          },
          {
          name: PLAN_SETUP_FIELD.REPLENISHMENT_MIN_VALUE,
          type: FieldTypes.Currency,
          disabled: !isEditMode,
          validator: currencyValueValidate(0).required(REQUIRED_TEXT),
          placeholder: 'Select Minimum Value',
          label: 'Minimum value',
          showRequireIcon: true,
          value: sourceState?.replenishmentMinValue ?? orgPrefundValues?.replenishmentMinValue,
        },
        {
          name: PLAN_SETUP_FIELD.REPLENISHMENT_CREEP_PERCENT,
          type: FieldTypes.Number,
          disabled: !isEditMode,
          showRequireIcon: true,
          validator: percentValueValidate.required(REQUIRED_TEXT),
          placeholder: 'Select Election Change Percentage',
          label: (
            <Box flex={{ grow: 1 }}>
              <Inscription margin={{ bottom: 'spacing4' }} weight="bold" color="textBody">
                Election change percentage
              </Inscription>
              <Box>
                <Inscription weight={500} color="textSecondary" size="small">
                  {PRE_FUND_SUBFIELDS.ELECTION_CHANGE_PERCENTAGE}
                </Inscription>
              </Box>
            </Box>
          ),
          value: sourceState?.replenishmentCreepPercent || orgPrefundValues?.replenishmentCreepPercent,
        },
        {
          name: PLAN_SETUP_FIELD.REPLENISHMENT_CREEP_MIN,
          type: FieldTypes.Currency,
          disabled: !isEditMode,
          validator: currencyValueValidate(0).required(REQUIRED_TEXT),
          placeholder: 'Select Minimum Threshold',
          label: 'Minimum threshold',
          showRequireIcon: true,
          fieldWrapperStyles: ALTERNATIVE_FIELD_WRAPPER_STYLES,
          value: sourceState?.replenishmentCreepMin ?? orgPrefundValues?.replenishmentCreepMin,
        }] : []
      ] : []),
      ] as Field[];
    }
    return [];
  }, [
    fundingMethod,
    isPreFundCalc,
    isEditMode,
    // isActivationMode,
    sourceState,
    // banksList,
    // isTemplateMode,
    orgPrefundValues,
  ]);

  const forfeitFields = useMemo(() => ([
    {
      name: 'isForfeitAllowed',
      type: FieldTypes.Checkbox,
      disabled: isForfeitDisabled,
      label: (
        <Box flex={{ grow: 1 }}>
          <Inscription margin={{ bottom: 'spacing4' }} weight="bold" color="textBody">
            Forfeit
          </Inscription>
          <Box>
            <Inscription weight={500} color="textSecondary" size="12px" lineHeight="18px">
              The forfeit option might be disabled when 'Run Out' is set to 'No Claims Deadline'.
            </Inscription>
          </Box>
        </Box>
      ),
      ...(isForfeitDisabled ? {
        name: 'isForfeitAllowed_disabled',
      } : {}),
    },
    ...(isForfeitDisabled || !isForfeitAllowed ? [] : [{
      name: 'forfeitureDays',
      type: FieldTypes.Number,
      placeholder: 'Enter # of days',
      label: (
        <Box flex={{ grow: 1 }}>
          <Inscription margin={{ bottom: 'spacing4' }} weight="bold" color="textBody">
            Forfeiture Days
          </Inscription>
          <Box>
            <Inscription weight={500} color="textSecondary" size="12px" lineHeight="18px">
              Forfeiture will occur a number of days after plan can no longer pay out claims (after any runout and rollover have completed).
            </Inscription>
          </Box>
        </Box>
      ),
      validator: yup.string().test({
        test: (val) => {
          if (runOut === RunOutOption.LAST_DAY_OF_PLAN_YEAR || runOut === RunOutOption.CUSTOM) {
            return _.toNumber(val) >= 1;
          }
          return true;
        },
        message: 'Number of the forfeiture days should be 1 or more.',
      }),
    }]),
  ]), [runOut, isForfeitAllowed, isForfeitDisabled]);

  const maintenanceFeeFields = useMemo(() => [
    {
      name: 'maintenanceFee',
      showRequireIcon: true,
      type: FieldTypes.Currency,
      label: 'Account maintenance fee',
      placeholder: 'Enter $ amount',
      validator: yup
        .string()
        .trim()
        .test({
          test: (val) => {
            if (val) {
              return _.toNumber(val) > 0;
            }
            return true;
          },
          message: MUST_BE_GREATER_TEXT(0),
        })
        .required(REQUIRED_TEXT),
    },
    {
      name: 'applyFeeTo',
      type: FieldTypes.Radio,
      showRequireIcon: true,
      label: 'Apply fee to',
      options: APPLY_FEE_TO_OPTIONS,
      validator:  yup.string().trim().nullable().required(REQUIRED_TEXT),
    },
    {
      name: 'cashBalanceThreshold',
      type: FieldTypes.Currency,
      label: 'Cash balance threshold',
      placeholder: 'Enter $ amount',
      validator: yup
        .string()
        .trim()
        .test({
          test: (val) => {
            if (val) {
              return _.toNumber(val) > 0;
            }
            return true;
          },
          message: MUST_BE_GREATER_TEXT(0),
        }),
    },
  ], []);
  const financeFields = useFinancialDetailsParamsFields(isEditMode, isActivationMode, isTemplateMode);
  const EOYFields = useEOYPlanReconciliationFields(
    isEditMode, 
    fundingMethod || undefined, 
    currentState?.omnibus || undefined
  );

  const payrollContributionFields = usePayrollContributionFields({
    isLastSection: fundingMethod === FUNDING_METHODS.CONTRIBUTION_ONLY || !isPreFundCalc,
  }, isActivationMode);
  const isPayrollContributionFieldsVisible = useMemo(
    () => (fundingMethod === FUNDING_METHODS.PREFUND_AND_CONTRIBUTION
    || fundingMethod === FUNDING_METHODS.CONTRIBUTION_ONLY && isPlanLevelFunding),
  [fundingMethod, isPlanLevelFunding]);

  const fields = useMemo(() => [
    ...financeFields,
    {
      name: 'isParticipantPaidMaintenanceFee',
      type: FieldTypes.Checkbox,
      label: 'Participant paid maintenance fee',
    },
    ...(isParticipantPaidMaintenanceFee ? maintenanceFeeFields : []),

    ...fundingReplenishmentFields,
    ...(isPayrollContributionFieldsVisible ? payrollContributionFields : []),
    ...preFundFields,

    {
      name: 'omnibus',
      type: FieldTypes.Dropdown,
      label: 'Omnibus',
      singleMode: true,
      options: OMNIBUS_OPTIONS,
      placeholder: 'Select option',
      showRequireIcon: true,
      validator: isActivationMode
        ? VALIDATORS.REQUIRED_STRING.
            test({
              test: (val) => {
                if (planType === HSA_PLAN_TYPE && isFundingMethodsFF) {
                  return val === `${OMNIBUS_TYPES.HSA}`;
                }
                return true;
              },
              message: 'Omnibus must be HSA for plan type \'HSA\'.',
            })
        : VALIDATORS.STRING.
            test({
              test: (val) => {
                if (planType === HSA_PLAN_TYPE && isFundingMethodsFF) {
                  return val === `${OMNIBUS_TYPES.HSA}`;
                }
                return true;
              },
              message: 'Omnibus must be HSA for plan type \'HSA\'.',
            }),
    },
    {
      name: 'accountCreation',
      type: FieldTypes.Dropdown,
      label: 'Account creation',
      singleMode: true,
      options: [{ key: 'REUSE_WHEN_SAME_ACCOUNT', value: 'Re-use when same account type' }, { key: 'CREATE_NEW', value: 'Always create new' }],
      placeholder: 'Select option',
      showRequireIcon: true,
      validator: isActivationMode
        ? VALIDATORS.REQUIRED_STRING
        : VALIDATORS.STRING,
    },
    ...forfeitFields,
    ...EOYFields,
  ] as Field[], [
    fundingReplenishmentFields,
    isActivationMode,
    preFundFields,
    forfeitFields,
    isParticipantPaidMaintenanceFee,
    maintenanceFeeFields,
    financeFields,
    planType,
    isFundingMethodsFF,
    EOYFields,
    payrollContributionFields,
    isPayrollContributionFieldsVisible,
  ]);
  const filteredField = usePlanFieldsState(fields);
  const formattedFields = useFieldsWithDefaultValues(filteredField, appliedSourceState);

  const stateFields = useTemplateFieldState(formattedFields, isTemplateMode, isEditMode);
  return stateFields;
};

export default useAccountFundingParamsFields;
